import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

/**
 * New Relic Browser API types
 */
declare global {
  interface Window {
    newrelic?: {
      setCustomAttribute: (
        name: string,
        value: string | number | boolean
      ) => void;
      setUserId: (userId: string) => void;
      setErrorHandler: (handler: (err: Error) => boolean) => void;
      noticeError: (
        error: Error,
        customAttributes?: Record<string, string | number | boolean>
      ) => void;
      addPageAction: (
        name: string,
        attributes?: Record<string, string | number | boolean>
      ) => void;
      setPageViewName: (name: string, host?: string) => void;
      addRelease: (releaseName: string, releaseId: string) => void;
      interaction: () => BrowserInteraction;
      finished: (time?: number) => void;
    };
  }
}

interface BrowserInteraction {
  setName: (name: string) => BrowserInteraction;
  setAttribute: (
    name: string,
    value: string | number | boolean
  ) => BrowserInteraction;
  save: () => BrowserInteraction;
  ignore: () => BrowserInteraction;
  end: () => void;
}

/**
 * Service for interacting with New Relic Browser Agent
 * Provides methods for custom attributes, error tracking, and user journey monitoring
 */
@Injectable({
  providedIn: 'root'
})
export class NewRelicService {
  private get nr() {
    return window.newrelic;
  }

  private get isEnabled(): boolean {
    return environment.newRelic?.enabled && !!this.nr;
  }

  /**
   * Set the current user ID for session tracking
   */
  setUserId(userId: string): void {
    if (this.isEnabled) {
      this.nr.setUserId(userId);
    }
  }

  /**
   * Set custom attributes that will be attached to all events
   */
  setCustomAttribute(name: string, value: string | number | boolean): void {
    if (this.isEnabled) {
      this.nr.setCustomAttribute(name, value);
    }
  }

  /**
   * Set multiple custom attributes at once
   */
  setCustomAttributes(
    attributes: Record<string, string | number | boolean>
  ): void {
    if (this.isEnabled) {
      Object.entries(attributes).forEach(([key, value]) => {
        this.nr.setCustomAttribute(key, value);
      });
    }
  }

  /**
   * Set user context (call after login)
   */
  setUserContext(user: {
    id: number | string;
    email?: string;
    role?: string;
    companyId?: number;
  }): void {
    if (this.isEnabled) {
      this.nr.setUserId(String(user.id));
      this.setCustomAttributes({
        userId: user.id,
        ...(user.email && { userEmail: user.email }),
        ...(user.role && { userRole: user.role }),
        ...(user.companyId && { companyId: user.companyId })
      });
    }
  }

  /**
   * Clear user context (call on logout)
   */
  clearUserContext(): void {
    if (this.isEnabled) {
      this.nr.setUserId('');
      this.setCustomAttributes({
        userId: '',
        userEmail: '',
        userRole: '',
        companyId: ''
      });
    }
  }

  /**
   * Report a JavaScript error with optional custom attributes
   */
  noticeError(
    error: Error,
    customAttributes?: Record<string, string | number | boolean>
  ): void {
    if (this.isEnabled) {
      this.nr.noticeError(error, customAttributes);
    }
  }

  /**
   * Record a custom page action/event
   */
  addPageAction(
    name: string,
    attributes?: Record<string, string | number | boolean>
  ): void {
    if (this.isEnabled) {
      this.nr.addPageAction(name, attributes);
    }
  }

  /**
   * Set a custom page view name (useful for SPA route tracking)
   */
  setPageViewName(name: string): void {
    if (this.isEnabled) {
      this.nr.setPageViewName(name);
    }
  }

  /**
   * Track certificate generation events
   */
  trackCertificateGeneration(certificateType: string, orderId: number): void {
    this.addPageAction('CertificateGeneration', {
      certificateType,
      orderId
    });
  }

  /**
   * Track inspection form events
   */
  trackInspectionEvent(
    action: 'start' | 'save' | 'complete' | 'error',
    inspectionId: number,
    additionalAttributes?: Record<string, string | number | boolean>
  ): void {
    this.addPageAction(`Inspection_${action}`, {
      inspectionId,
      ...additionalAttributes
    });
  }

  /**
   * Track file upload events
   */
  trackFileUpload(
    fileType: string,
    fileSizeBytes: number,
    success: boolean
  ): void {
    this.addPageAction('FileUpload', {
      fileType,
      fileSizeBytes,
      success
    });
  }

  /**
   * Start a custom interaction for tracking complex user flows
   */
  startInteraction(name: string): BrowserInteraction | null {
    if (this.isEnabled) {
      return this.nr.interaction().setName(name);
    }
    return null;
  }

  /**
   * Mark the page as finished loading (for custom SPA timing)
   */
  markPageLoadComplete(): void {
    if (this.isEnabled) {
      this.nr.finished();
    }
  }

  /**
   * Add release/version information
   */
  setRelease(releaseName: string, releaseId: string): void {
    if (this.isEnabled) {
      this.nr.addRelease(releaseName, releaseId);
    }
  }
}
